import { Icon } from "@iconify/react";
import { motion, AnimatePresence, useInView } from "framer-motion";
import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

const Color = {
  studyYellow: "#F7B500",
  studyGreen: "#2EB67D",
  themeBlue: "#4ea4f5",
  themeGreen: "#52c41a",
  textPrimary: "#000",
  borderLight: "#e0e0e0",
  white: "#fff",
  bgLight: "#f8f9fa",
  correctGreen: "#ccffcc",
  incorrectRed: "#ffe6e6",
  cancelRed: "#ff4d4f",
  citationGray: "#f5f5f5",
};

interface FeatureSectionStudyToolsProps {
  showDescription?: boolean;
}

const FeatureSectionStudyTools: React.FC<FeatureSectionStudyToolsProps> = ({ showDescription = true }) => {
  const { t } = useTranslation();
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [flashcardRevealed, setFlashcardRevealed] = useState(false);
  const [showHint, setShowHint] = useState(false);
  const [showBulletPoints, setShowBulletPoints] = useState(false);
  const [bulgingElement, setBulgingElement] = useState<string | null>(null); // Bulge preview before click
  const animationAbortRef = useRef(false);
  const animationStartedRef = useRef(false);
  const bulletPointsShownRef = useRef<boolean>(false); // Track if bullet points have ever been shown
  const quizScrollRef = useRef<HTMLDivElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  // Only animate when 90% of card is visible (Google's standard)
  const isInView = useInView(cardRef, { amount: 0.9 });

  const quizQuestion = {
    question: t("landing.features.study.quiz.question"),
    options: [
      t("landing.features.study.quiz.option0"),
      t("landing.features.study.quiz.option1"),
      t("landing.features.study.quiz.option2"),
      t("landing.features.study.quiz.option3"),
    ],
    correct: 3, // D is correct - iOS, Android, and Web
    explanation: t("landing.features.study.quiz.explanation"),
    citation: { label: "[02:30]", type: "time" },
  };

  const flashcard = {
    front: t("modern.featureSections.study.mockContent.flashcardFront"),
    hint: t("modern.featureSections.study.mockContent.flashcardHint"),
    back: t("modern.featureSections.study.mockContent.flashcardBack"),
    citation: { label: "[05:15]", type: "time" },
  };

  const isCorrect = selectedAnswer === quizQuestion.correct;
  const isAnswered = selectedAnswer !== null;

  // Wait helper - simple single setTimeout (no polling)
  const wait = (ms: number) => new Promise<boolean>(resolve => {
    setTimeout(() => {
      if (animationAbortRef.current) resolve(false);
      else resolve(true);
    }, ms);
  });

  // Animation loop
  useEffect(() => {
    if (!isInView) {
      animationStartedRef.current = false;
      return;
    }
    if (animationStartedRef.current) return;
    animationStartedRef.current = true;

    let isMounted = true;
    animationAbortRef.current = false;

    const runAnimation = async () => {
      while (isMounted && !animationAbortRef.current) {
        // Reset card state
        setSelectedAnswer(null);
        setFlashcardRevealed(false);
        setShowHint(false);
        // Only hide bullet points if they haven't been shown yet
        if (!bulletPointsShownRef.current) {
          setShowBulletPoints(false);
        }
        setBulgingElement(null);
        if (quizScrollRef.current) {
          quizScrollRef.current.scrollTo({ top: 0 });
        }

        // Start animation immediately
        if (!await wait(0)) continue;

        // STEP 1: Bulge on answer D before clicking
        setBulgingElement("answer-3");
        if (!await wait(900)) continue;

        // Select answer D (index 3)
        setBulgingElement(null);
        setSelectedAnswer(3);
        if (!await wait(600)) continue;

        // STEP 2: Scroll to show explanation
        if (quizScrollRef.current) {
          quizScrollRef.current.scrollTo({
            top: quizScrollRef.current.scrollHeight,
            behavior: "smooth",
          });
        }
        if (!await wait(1500)) continue;

        // STEP 3: Bulge on Show Hint button before clicking
        setBulgingElement("show-hint");
        if (!await wait(900)) continue;

        // Show hint on flashcard
        setBulgingElement(null);
        setShowHint(true);
        if (!await wait(450)) continue;

        // STEP 4: Bulge on Reveal Answer before flipping
        setBulgingElement("reveal-answer");
        if (!await wait(600)) continue;

        // Flip flashcard to reveal answer
        setBulgingElement(null);
        setFlashcardRevealed(true);
        if (!await wait(900)) continue;

        // Animation complete - show bullet points and mark as shown
        setShowBulletPoints(true);
        bulletPointsShownRef.current = true;

        // Wait for reading bullet points (~5 seconds)
        if (!await wait(5000)) continue;

        // Wait before restarting loop (8 seconds)
        if (!await wait(8000)) continue;
      }
    };

    runAnimation();

    return () => {
      isMounted = false;
      animationAbortRef.current = true;
    };
  }, [isInView]);

  return (
    <section className={`${showDescription ? 'py-8 md:py-20' : 'py-0'} bg-white overflow-hidden`}>
      <div className={showDescription ? "container mx-auto px-4" : ""}>
        <div className={`${showDescription ? 'grid md:grid-cols-2 gap-12 lg:gap-20 items-center max-w-7xl mx-auto' : 'flex justify-center'}`}>
          {/* Left - Visual Mockup */}
          <motion.div
            ref={cardRef}
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="order-2 px-4 md:px-0"
          >
            {/* Card wrapper with relative positioning for badge */}
            <div style={{ position: "relative", width: "fit-content" }}>
              <div className="space-y-6">
              {/* Quiz Card - Fixed height with scroll */}
              <motion.div
                className="rounded-xl overflow-hidden shadow-lg"
                style={{
                  backgroundColor: Color.white,
                  border: `1px solid ${Color.borderLight}`,
                }}
                whileHover={{ y: -2, boxShadow: "0 8px 24px rgba(0,0,0,0.1)" }}
                transition={{ duration: 0.2 }}
              >
                {/* Quiz Header */}
                <div
                  className="px-4 py-3 flex items-center justify-between"
                  style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                >
                  <div className="flex items-center gap-2">
                    <Icon icon="lucide:clipboard-list" style={{ color: Color.studyGreen }} />
                    <span className="font-semibold text-gray-900">{t("landing.features.study.quiz.headerLabel")}</span>
                  </div>
                  <span className="text-sm text-gray-500">{t("modern.featureSections.study.mockContent.quizProgress")}</span>
                </div>

                {/* Quiz Content - Fixed height with scroll */}
                <div
                  ref={quizScrollRef}
                  className="p-4 overflow-y-auto"
                  style={{
                    height: 340,
                    scrollbarWidth: "none",
                    msOverflowStyle: "none",
                  }}
                >
                  <p className="text-gray-900 font-semibold mb-4 leading-relaxed">
                    {quizQuestion.question}
                  </p>

                  {/* Options - Matching mobile app styling */}
                  <div className="space-y-2">
                    {quizQuestion.options.map((option, index) => {
                      const isSelected = selectedAnswer === index;
                      const isCorrectOption = index === quizQuestion.correct;
                      const showAsCorrect = isAnswered && isCorrectOption;
                      const showAsIncorrect = isSelected && !isCorrect;

                      return (
                        <motion.button
                          key={index}
                          className="w-full text-left p-3 rounded-xl transition-all"
                          style={{
                            border: `2px solid ${
                              showAsCorrect
                                ? Color.themeGreen
                                : showAsIncorrect
                                ? Color.cancelRed
                                : isSelected
                                ? Color.themeBlue
                                : "#e0e0e0"
                            }`,
                            backgroundColor: Color.white,
                          }}
                          onClick={() => !isAnswered && setSelectedAnswer(index)}
                          whileTap={!isAnswered ? { scale: 0.98 } : {}}
                          animate={
                            bulgingElement === `answer-${index}`
                              ? { scale: [1, 1.03, 1.03], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 15px rgba(78, 164, 245, 0.35)", "0 0 15px rgba(78, 164, 245, 0.35)"] }
                              : showAsIncorrect
                                ? { x: [0, -5, 5, -5, 5, 0], scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                                : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                          }
                          transition={{ duration: bulgingElement === `answer-${index}` ? 0.5 : 0.3, ease: "easeOut" }}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-medium"
                              style={{
                                backgroundColor: showAsCorrect
                                  ? Color.themeGreen
                                  : showAsIncorrect
                                  ? Color.cancelRed
                                  : isSelected
                                  ? Color.themeBlue
                                  : "#f0f0f0",
                                color: isSelected || showAsCorrect || showAsIncorrect ? Color.white : "#666",
                              }}
                            >
                              {String.fromCharCode(65 + index)}
                            </span>
                            <span className="text-gray-700 flex-1">{option}</span>
                            {showAsCorrect && (
                              <Icon icon="lucide:check" className="text-green-600" />
                            )}
                            {showAsIncorrect && (
                              <Icon icon="lucide:x" className="text-red-500" />
                            )}
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>

                  {/* Explanation with Citation - Matching mobile app styling */}
                  <AnimatePresence>
                    {isAnswered && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="mt-4 p-3 rounded-lg"
                        style={{
                          backgroundColor: isCorrect ? "#F0F9F4" : "#FFF2F2",
                        }}
                      >
                        <p
                          className="text-sm font-semibold mb-2"
                          style={{ color: isCorrect ? "#52c41a" : "#ff4d4f" }}
                        >
                          {isCorrect ? "Correct!" : "Explanation"}
                        </p>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {quizQuestion.explanation}
                        </p>
                        {/* Citation Badge */}
                        <button
                          className="mt-2 inline-flex items-center px-2 py-1 rounded"
                          style={{
                            backgroundColor: Color.citationGray,
                            border: `1px solid ${Color.borderLight}`,
                          }}
                        >
                          <span className="text-xs font-semibold" style={{ color: Color.textPrimary }}>
                            {quizQuestion.citation.label}
                          </span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </motion.div>

              {/* Flashcard - Full card flip */}
              <div
                className="cursor-pointer"
                style={{ perspective: 1000, height: 260 }}
                onClick={() => {
                  if (!showHint && !flashcardRevealed) {
                    setShowHint(true);
                  } else if (showHint && !flashcardRevealed) {
                    setFlashcardRevealed(true);
                  } else {
                    setFlashcardRevealed(false);
                    setShowHint(false);
                  }
                }}
              >
                <motion.div
                  className="relative w-full h-full"
                  style={{ transformStyle: "preserve-3d" }}
                  animate={{ rotateY: flashcardRevealed ? 180 : 0 }}
                  transition={{ duration: 0.5 }}
                >
                  {/* Front Face - Question */}
                  <div
                    className="absolute inset-0 rounded-xl overflow-hidden shadow-lg"
                    style={{
                      backgroundColor: Color.white,
                      border: `1px solid ${Color.borderLight}`,
                      boxShadow: "0 4px 16px rgba(78, 164, 245, 0.15)",
                      backfaceVisibility: "hidden",
                    }}
                  >
                    {/* Header */}
                    <div
                      className="px-4 py-3 flex items-center justify-between"
                      style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon icon="lucide:layers" style={{ color: Color.studyYellow }} />
                        <span className="font-semibold text-gray-900">{t("landing.features.study.flashcard.headerLabel")}</span>
                      </div>
                      <span className="text-sm text-gray-500">{t("landing.features.study.flashcard.tapToFlip")}</span>
                    </div>
                    {/* Content */}
                    <div className="p-5 flex flex-col justify-center" style={{ height: "calc(100% - 52px)" }}>
                      <p className="text-lg font-semibold text-gray-900 mb-6">
                        {flashcard.front}
                      </p>
                      {/* Hint - shows before flipping */}
                      <AnimatePresence>
                        {showHint && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                            className="mb-3 px-3 py-2 rounded-lg"
                            style={{ backgroundColor: `${Color.studyYellow}20` }}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <Icon icon="lucide:lightbulb" className="text-sm" style={{ color: Color.studyYellow }} />
                              <span className="text-xs font-semibold" style={{ color: Color.studyYellow }}>Hint</span>
                            </div>
                            <p className="text-sm text-gray-600 italic">{flashcard.hint}</p>
                          </motion.div>
                        )}
                      </AnimatePresence>
                      {/* Show Hint button first, then Reveal Answer */}
                      {!showHint ? (
                        <motion.button
                          className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold"
                          style={{
                            border: `2px solid ${Color.studyYellow}`,
                            color: Color.studyYellow,
                            backgroundColor: "transparent",
                          }}
                          animate={
                            bulgingElement === "show-hint"
                              ? { scale: [1, 1.05, 1.05], boxShadow: ["0 0 0 rgba(247, 181, 0, 0)", "0 0 15px rgba(247, 181, 0, 0.4)", "0 0 15px rgba(247, 181, 0, 0.4)"] }
                              : { scale: 1, boxShadow: "0 0 0 rgba(247, 181, 0, 0)" }
                          }
                          transition={{ duration: bulgingElement === "show-hint" ? 0.5 : 0.2, ease: "easeOut" }}
                        >
                          <span className="flex items-center justify-center gap-2">
                            <Icon icon="lucide:lightbulb" className="text-sm" />
                            {t("landing.features.study.flashcard.showHintButton")}
                          </span>
                        </motion.button>
                      ) : (
                        <motion.button
                          className="w-full py-2.5 px-4 rounded-lg text-sm font-semibold"
                          style={{
                            border: `2px solid ${Color.themeBlue}`,
                            color: Color.themeBlue,
                            backgroundColor: "transparent",
                          }}
                          animate={
                            bulgingElement === "reveal-answer"
                              ? { scale: [1, 1.05, 1.05], boxShadow: ["0 0 0 rgba(78, 164, 245, 0)", "0 0 15px rgba(78, 164, 245, 0.4)", "0 0 15px rgba(78, 164, 245, 0.4)"] }
                              : { scale: 1, boxShadow: "0 0 0 rgba(78, 164, 245, 0)" }
                          }
                          transition={{ duration: bulgingElement === "reveal-answer" ? 0.5 : 0.2, ease: "easeOut" }}
                        >
                          {t("landing.features.study.flashcard.revealAnswerButton")}
                        </motion.button>
                      )}
                    </div>
                  </div>

                  {/* Back Face - Answer */}
                  <div
                    className="absolute inset-0 rounded-xl overflow-hidden shadow-lg"
                    style={{
                      backgroundColor: Color.white,
                      border: `1px solid ${Color.borderLight}`,
                      boxShadow: "0 4px 16px rgba(78, 164, 245, 0.15)",
                      backfaceVisibility: "hidden",
                      transform: "rotateY(180deg)",
                    }}
                  >
                    {/* Header */}
                    <div
                      className="px-4 py-3 flex items-center justify-between"
                      style={{ borderBottom: `1px solid ${Color.borderLight}` }}
                    >
                      <div className="flex items-center gap-2">
                        <Icon icon="lucide:layers" style={{ color: Color.studyYellow }} />
                        <span className="font-semibold text-gray-900">{t("landing.features.study.flashcard.headerLabel")}</span>
                      </div>
                      <span className="text-sm text-gray-500">Answer</span>
                    </div>
                    {/* Content */}
                    <div className="p-4 flex flex-col" style={{ height: "calc(100% - 52px)" }}>
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                        Answer
                      </p>
                      <p className="text-sm text-gray-700 leading-relaxed flex-1">
                        {flashcard.back}
                      </p>
                      {/* Footer buttons */}
                      <div className="flex items-center justify-center gap-2 pt-2 border-t" style={{ borderTopColor: Color.borderLight }}>
                        <button className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-red-500 hover:bg-red-50">
                          <Icon icon="lucide:x-circle" className="text-sm" />
                          <span>{t("modern.featureSections.study.mockContent.againButton")}</span>
                        </button>
                        <button className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-yellow-600 hover:bg-yellow-50">
                          <Icon icon="lucide:minus-circle" className="text-sm" />
                          <span>{t("modern.featureSections.study.mockContent.hardButton")}</span>
                        </button>
                        <button className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-green-500 hover:bg-green-50">
                          <Icon icon="lucide:check-circle" className="text-sm" />
                          <span>{t("modern.featureSections.study.mockContent.goodButton")}</span>
                        </button>
                        <button className="flex items-center gap-1 px-2 py-1 rounded-lg text-xs text-gray-500 hover:bg-gray-50">
                          <Icon icon="lucide:zap" className="text-sm" />
                          <span>{t("modern.featureSections.study.mockContent.easyButton")}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>

              {/* Floating badge - inside wrapper */}
              <motion.div
                className="px-4 py-2 rounded-full shadow-lg flex items-center gap-2"
                style={{
                  position: "absolute",
                  bottom: -6,
                  left: -6,
                  backgroundColor: Color.studyYellow,
                  zIndex: 10,
                }}
                animate={{ y: [0, -5, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Icon icon="lucide:brain" className="text-white" />
                <span className="text-white text-sm font-medium">{t("landing.features.study.badge")}</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Right - Text Content */}
          {showDescription && <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="order-1"
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
              style={{ backgroundColor: `${Color.studyYellow}15` }}
            >
              <Icon icon="lucide:brain" style={{ color: Color.studyYellow }} />
              <span style={{ color: Color.studyYellow }} className="font-medium text-sm">
                {t("landing.featureDetail.study.title")}
              </span>
            </div>

            <h2 className="text-11xl md:text-17xl lg:text-29xl font-bold text-gray-900 mb-6 leading-tight mt-0">
              {t("landing.features.study.headline")}
            </h2>

            <p className="text-lg md:text-xl mb-8 leading-relaxed" style={{ color: "#726F6F" }}>
              {t("landing.featureDetail.study.description")}
            </p>

            {/* Highlights - always visible on mobile, animated on desktop */}
            {/* Mobile version - always visible */}
            <div className="block md:hidden space-y-4">
              {[
                { icon: "lucide:wand-2", text: t("landing.featureDetail.study.highlight1") },
                { icon: "lucide:repeat", text: t("landing.featureDetail.study.highlight2") },
                { icon: "lucide:trending-up", text: t("landing.featureDetail.study.highlight3") },
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div
                    className="w-9 h-9 rounded-lg flex items-center justify-center"
                    style={{ backgroundColor: `${Color.studyYellow}15` }}
                  >
                    <Icon icon={item.icon} className="text-lg" style={{ color: Color.studyYellow }} />
                  </div>
                  <span className="text-base text-gray-700">{item.text}</span>
                </div>
              ))}
            </div>
            {/* Desktop version - animated after card animation completes */}
            <div className="hidden md:block" style={{ minHeight: 140 }}>
              <motion.div
                className="space-y-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: showBulletPoints ? 1 : 0 }}
                transition={{ duration: 0.4 }}
              >
                {[
                  { icon: "lucide:wand-2", text: t("landing.featureDetail.study.highlight1") },
                  { icon: "lucide:repeat", text: t("landing.featureDetail.study.highlight2") },
                  { icon: "lucide:trending-up", text: t("landing.featureDetail.study.highlight3") },
                ].map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: showBulletPoints ? 1 : 0, x: showBulletPoints ? 0 : 20 }}
                    transition={{ delay: showBulletPoints ? index * 0.15 : 0, duration: 0.4 }}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${Color.studyYellow}15` }}
                    >
                      <Icon icon={item.icon} className="text-lg" style={{ color: Color.studyYellow }} />
                    </div>
                    <span className="text-base text-gray-700">{item.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>}
        </div>
      </div>
    </section>
  );
};

export default FeatureSectionStudyTools;
